import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';

interface Param {
  name: string;
  value: string;
}

function parseParams(raw: string[]): Param[] {
  return raw.map(entry => {
    const eqIdx = entry.indexOf('=');
    if (eqIdx === -1) return { name: entry, value: '' };
    return { name: entry.slice(0, eqIdx), value: entry.slice(eqIdx + 1) };
  });
}

function serializeParams(params: Param[]): string[] {
  return params.map(p => `${p.name}=${p.value}`);
}

interface AdditionalParamsModalProps {
  open: boolean;
  additionalParams: string[];
  onConfirm: (params: string[]) => void;
  onClose: () => void;
}

export const AdditionalParamsModal = ({
  open,
  additionalParams,
  onConfirm,
  onClose,
}: AdditionalParamsModalProps) => {
  const [params, setParams] = useState<Param[]>(() => parseParams(additionalParams));

  useEffect(() => {
    if (open) {
      setParams(parseParams(additionalParams));
    }
  }, [open, additionalParams]);

  const handleChange = (index: number, value: string) => {
    setParams(prev => {
      const next = [...prev];
      next[index] = { ...next[index], value };
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure Parameters</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Review and adjust the workflow parameters before triggering.
        </Typography>
        <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8 }}>
          {params.map((param, idx) => (
            <TextField
              key={`${param.name}-${idx}`}
              label={param.name}
              value={param.value}
              onChange={e => handleChange(idx, e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onConfirm(serializeParams(params))}
          color="primary"
          variant="contained"
        >
          Trigger
        </Button>
      </DialogActions>
    </Dialog>
  );
};
